import { PAGE_CONTAINER_MAX_WIDTH } from '../constants/documentTheme';
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  TextField,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Divider,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Collapse,
  IconButton,
  Breadcrumbs,
  Link,
  Drawer
} from '@mui/material';
import { Schedule, Edit, Close, FilterList } from '@mui/icons-material';
import FlightPriceGrid from '../components/FlightPriceGrid';
import FlightSearchForm from '../components/FlightSearchForm';
import api from '../config/api';

function FlightSearchResults() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [flights, setFlights] = useState([]);
  const [filteredFlights, setFilteredFlights] = useState([]);
  
  // Multi-step selection for round trips (Duffel-style: show outbound, then inbound from same bundled offers)
  const [selectionStep, setSelectionStep] = useState(1); // 1 = show outbound leg, 2 = show inbound leg
  const [selectedOffer, setSelectedOffer] = useState(null); // Store the selected bundled offer
  const [validatingOfferId, setValidatingOfferId] = useState(null); // Track which offer is being validated
  
  // Search parameters (from URL)
  const urlOrigin = searchParams.get('origin') || '';
  const urlDestination = searchParams.get('destination') || '';
  const urlDepartDate = searchParams.get('departDate') || '';
  const urlReturnDate = searchParams.get('returnDate') || '';
  const urlPassengers = parseInt(searchParams.get('passengers') || '1');
  const urlTripType = searchParams.get('tripType') || 'round_trip';
  
  // Editable search form states
  const [editMode, setEditMode] = useState(false);
  const [origin, setOrigin] = useState(urlOrigin);
  const [destination, setDestination] = useState(urlDestination);
  const [departDate, setDepartDate] = useState(urlDepartDate);
  const [returnDate, setReturnDate] = useState(urlReturnDate);
  const [passengers, setPassengers] = useState(urlPassengers);
  const [tripType, setTripType] = useState(urlTripType);
  
  // Filter drawer state
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  
  // Filter states
  const [sortBy, setSortBy] = useState('least_expensive');
  const [maxStops, setMaxStops] = useState('any');
  const [selectedAirline, setSelectedAirline] = useState('all');
  const [flightNumber, setFlightNumber] = useState('');
  
  // Price grid visibility (disabled by default to avoid rate limiting)
  // Persist state across searches using sessionStorage
  const [showPriceGrid, setShowPriceGrid] = useState(() => {
    try {
      return sessionStorage.getItem('showPriceGrid') === 'true';
    } catch {
      return false;
    }
  });
  
  // Save price grid state to sessionStorage when it changes
  useEffect(() => {
    try {
      sessionStorage.setItem('showPriceGrid', showPriceGrid.toString());
    } catch {
      // Ignore storage errors
    }
  }, [showPriceGrid]);
  
  // Separate time filters for outbound and return legs
  const [outboundDepartTime, setOutboundDepartTime] = useState([0, 24]);
  const [outboundArrivalTime, setOutboundArrivalTime] = useState([0, 24]);
  const [returnDepartTime, setReturnDepartTime] = useState([0, 24]);
  const [returnArrivalTime, setReturnArrivalTime] = useState([0, 24]);
  
  // Get unique airlines from results
  const airlines = [...new Set(flights.map(f => f.airline))].filter(Boolean);

  // These functions are now handled by the FlightSearchForm component

  // Update form fields when URL changes
  useEffect(() => {
    setOrigin(urlOrigin);
    setDestination(urlDestination);
    setDepartDate(urlDepartDate);
    setReturnDate(urlReturnDate);
    setPassengers(urlPassengers);
    setTripType(urlTripType);
  }, [urlOrigin, urlDestination, urlDepartDate, urlReturnDate, urlPassengers, urlTripType]);

  useEffect(() => {
    const searchFlights = async () => {
      // Set a timeout to prevent infinite loading
      const timeoutId = setTimeout(() => {
        console.error('⏱️ Search timeout - forcing loading to stop');
        setLoading(false);
        alert('Search is taking longer than expected. Please try again or contact support if this persists.');
      }, 30000); // 30 second timeout

      try {
        setLoading(true);
        console.log('🔍 Starting flight search...');
        
        // Convert city names to IATA codes if needed
        const { convertToIATA } = await import('../utils/airportDatabase');
        const originIATA = await convertToIATA(urlOrigin);
        const destinationIATA = await convertToIATA(urlDestination);
        
        if (!originIATA || !destinationIATA) {
          throw new Error(`Unable to find airport codes for ${!originIATA ? urlOrigin : urlDestination}`);
        }
        
        console.log(`🔍 Converted: ${urlOrigin} → ${originIATA}, ${urlDestination} → ${destinationIATA}`);
        
        // Build search payload - for round trips, include return_date to get bundled offers
        const searchPayload = {
          origin: originIATA,
          destination: destinationIATA,
          departure_date: urlDepartDate,
          passengers: urlPassengers,
          cabin_class: 'economy'
        };
        
        // For round trips, add return_date to get bundled offers with 2 slices
        if (urlTripType === 'round_trip' && urlReturnDate) {
          searchPayload.return_date = urlReturnDate;
          console.log('🔍 Searching bundled round-trip offers:', searchPayload);
        } else {
          console.log('🔍 Searching one-way flights:', searchPayload);
        }
        
        const response = await api.post('/duffel/search', searchPayload);
        clearTimeout(timeoutId); // Clear timeout on successful response
        
        console.log('✈️ Duffel search response:', response.data.total_offers, 'offers');
        console.log('✈️ Response data:', response.data);
        console.log('✈️ First flight:', response.data.flights?.[0]);
        
        // Group flights by flight number/time to consolidate fare options
        const flights = response.data.flights || [];
        const flightGroups = new Map();
        
        flights.forEach((flight, idx) => {
          // Group ONLY by outbound flight characteristics
          // Don't include return flight in the key - we want to show ONE card per outbound flight
          // with all fare options across all possible return flights
          const depTime = flight.departure_time ? new Date(flight.departure_time) : null;
          const arrTime = flight.arrival_time ? new Date(flight.arrival_time) : null;
          
          // Round to nearest hour for grouping (handles slight time differences)
          const depHour = depTime ? `${depTime.toISOString().split('T')[0]}T${String(depTime.getUTCHours()).padStart(2, '0')}:00` : '';
          const arrHour = arrTime ? `${arrTime.toISOString().split('T')[0]}T${String(arrTime.getUTCHours()).padStart(2, '0')}:00` : '';
          
          const flightKey = [
            flight.segments?.[0]?.marketing_carrier_code,
            flight.segments?.[0]?.marketing_flight_number,
            depHour,
            arrHour,
            flight.origin,
            flight.destination,
            flight.stops
          ].join('|');
          
          // Debug logging for first 5 flights
          if (idx < 5) {
            console.log(`\n🔍 Flight ${idx + 1}:`);
            console.log(`  Key: ${flightKey}`);
            console.log(`  Price: $${flight.total_amount}`);
            console.log(`  Outbound Fare: ${flight.slices?.[0]?.fare_brand_name || 'N/A'}`);
            console.log(`  Inbound Fare: ${flight.slices?.[1]?.fare_brand_name || 'N/A'}`);
            console.log(`  Return Flight: ${flight.return_flight ? 'Yes' : 'No'}`);
          }
          
          // Group flights with the same key (same flight, different fares)
          if (!flightGroups.has(flightKey)) {
            flightGroups.set(flightKey, {
              ...flight,
              fareOptions: [flight], // Store all fare variations
              fareBrands: new Set([flight.slices?.[0]?.fare_brand_name || 'Economy']), // Track unique fare brands
              lowestPrice: parseFloat(flight.total_amount || 0),
              highestPrice: parseFloat(flight.total_amount || 0),
              fareCount: 1
            });
          } else {
            const group = flightGroups.get(flightKey);
            const fareBrand = flight.slices?.[0]?.fare_brand_name || 'Economy';
            
            // Only add if it's a DIFFERENT fare brand (not just a different price)
            if (!group.fareBrands.has(fareBrand)) {
              group.fareOptions.push(flight);
              group.fareBrands.add(fareBrand);
              group.fareCount++;
              
              // Track price range
              const price = parseFloat(flight.total_amount || 0);
              if (price < group.lowestPrice) {
                group.lowestPrice = price;
                // Update the main flight display to show cheapest option details
                Object.assign(group, {
                  ...flight,
                  fareOptions: group.fareOptions,
                  fareBrands: group.fareBrands,
                  fareCount: group.fareCount,
                  lowestPrice: group.lowestPrice,
                  highestPrice: group.highestPrice
                });
              }
              if (price > group.highestPrice) {
                group.highestPrice = price;
              }
            }
            // If same fare brand, skip it (it's a duplicate with slightly different return flight)
          }
        });
        
        // Convert map to array, sorted by lowest price
        const groupedFlights = Array.from(flightGroups.values())
          .sort((a, b) => a.lowestPrice - b.lowestPrice);
        
        console.log(`✅ Grouped: ${flights.length} offers → ${groupedFlights.length} unique flights`);
        groupedFlights.forEach((g, i) => {
          if (g.fareCount > 1) {
            console.log(`  ✈️  ${g.segments?.[0]?.marketing_carrier_code}${g.segments?.[0]?.marketing_flight_number}: ${g.fareCount} fares ($${g.lowestPrice.toFixed(2)} - $${g.highestPrice.toFixed(2)})`);
          }
        });
        
        setFlights(groupedFlights);
        setFilteredFlights(groupedFlights);
      } catch (error) {
        clearTimeout(timeoutId); // Clear timeout on error
        console.error('❌ Error searching flights:', error);
        console.error('Error details:', error.response?.data || error.message);
        alert(`Error searching flights: ${error.response?.data?.error || error.message}. Please try again.`);
      } finally {
        setLoading(false);
      }
    };

    if (urlOrigin && urlDestination && urlDepartDate) {
      searchFlights();
    }
  }, [urlOrigin, urlDestination, urlDepartDate, urlReturnDate, urlPassengers, urlTripType]);

  // Apply filters
  useEffect(() => {
    console.log('🔍 Applying filters to', flights.length, 'flights');
    let filtered = [...flights];

    // Filter by stops
    if (maxStops === 'direct') {
      filtered = filtered.filter(f => f.stops === 0);
    } else if (maxStops === '1') {
      filtered = filtered.filter(f => f.stops <= 1);
    } else if (maxStops === '2') {
      filtered = filtered.filter(f => f.stops <= 2);
    }

    // Filter by airline
    if (selectedAirline !== 'all') {
      filtered = filtered.filter(f => f.airline === selectedAirline);
    }

    // Filter by flight number
    if (flightNumber) {
      filtered = filtered.filter(f => 
        f.segments?.[0]?.marketing_flight_number?.toLowerCase().includes(flightNumber.toLowerCase())
      );
    }

    // Filter by outbound departure time
    filtered = filtered.filter(f => {
      const departTime = new Date(f.departure_time || f.segments?.[0]?.origin?.time);
      const hour = departTime.getHours();
      return hour >= outboundDepartTime[0] && hour <= outboundDepartTime[1];
    });

    // Filter by outbound arrival time
    filtered = filtered.filter(f => {
      const arriveTime = new Date(f.arrival_time || f.segments?.[f.segments?.length - 1]?.destination?.time);
      const hour = arriveTime.getHours();
      return hour >= outboundArrivalTime[0] && hour <= outboundArrivalTime[1];
    });

    // Filter by return departure and arrival times (for round trips)
    if (urlTripType === 'round_trip') {
      filtered = filtered.filter(f => {
        if (!f.return_flight) return true; // Skip if no return flight data
        
        const returnDepTime = new Date(f.return_flight.departure.time);
        const returnDepHour = returnDepTime.getHours();
        
        const returnArrTime = new Date(f.return_flight.arrival.time);
        const returnArrHour = returnArrTime.getHours();
        
        return (
          returnDepHour >= returnDepartTime[0] && 
          returnDepHour <= returnDepartTime[1] &&
          returnArrHour >= returnArrivalTime[0] && 
          returnArrHour <= returnArrivalTime[1]
        );
      });
    }

    // Sort
    if (sortBy === 'least_expensive') {
      filtered.sort((a, b) => parseFloat(a.total_amount) - parseFloat(b.total_amount));
    } else if (sortBy === 'most_expensive') {
      filtered.sort((a, b) => parseFloat(b.total_amount) - parseFloat(a.total_amount));
    } else if (sortBy === 'shortest') {
      filtered.sort((a, b) => {
        const durationA = parseDuration(a.duration);
        const durationB = parseDuration(b.duration);
        return durationA - durationB;
      });
    } else if (sortBy === 'longest') {
      filtered.sort((a, b) => {
        const durationA = parseDuration(a.duration);
        const durationB = parseDuration(b.duration);
        return durationB - durationA;
      });
    }

    console.log('✅ Filtered down to', filtered.length, 'flights');
    setFilteredFlights(filtered);
  }, [flights, sortBy, maxStops, selectedAirline, flightNumber, outboundDepartTime, outboundArrivalTime, returnDepartTime, returnArrivalTime, urlTripType]);

  const parseDuration = (duration) => {
    if (!duration) return 0;
    const match = duration.match(/(\d+)h\s*(\d+)?m?/);
    if (!match) return 0;
    const hours = parseInt(match[1] || 0);
    const minutes = parseInt(match[2] || 0);
    return hours * 60 + minutes;
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  // Calculate day difference between departure and arrival for overnight flights
  const getDayDifference = (departureTime, arrivalTime) => {
    if (!departureTime || !arrivalTime) return 0;
    const departure = new Date(departureTime);
    const arrival = new Date(arrivalTime);
    
    // Get calendar dates (ignoring time)
    const departureDate = new Date(departure.getFullYear(), departure.getMonth(), departure.getDate());
    const arrivalDate = new Date(arrival.getFullYear(), arrival.getMonth(), arrival.getDate());
    
    // Calculate difference in calendar days
    const diffMs = arrivalDate - departureDate;
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };

  const formatPrice = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const handleSelectFlight = async (flight) => {
    try {
      // For round trips, handle multi-step selection
      if (urlTripType === 'round_trip') {
        if (selectionStep === 1) {
          // Step 1: User selected outbound leg view, move to inbound leg view
          console.log('✅ Outbound leg selected, showing inbound options');
          setSelectedOffer(flight);
          setSelectionStep(2);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        } else if (selectionStep === 2) {
          // Step 2: User confirmed inbound leg, validate and navigate
          console.log('✅ Inbound leg confirmed, validating offer...');
          setValidatingOfferId(flight.id);
          
          try {
            // Validate offer is still available
            await api.get(`/duffel/offers/${flight.id}`);
            
            // Use the grouped fare options from the flight object
            const fareOptions = flight.fareOptions || [flight];
            console.log(`🎫 Flight has ${fareOptions.length} fare option(s)`);
            
            // Pass all fare option IDs (excluding the main one to avoid duplication)
            const alternativeIds = fareOptions
              .filter(f => f.id !== flight.id)
              .map(f => f.id)
              .join(',');
            
            const params = new URLSearchParams({
              passengers: urlPassengers.toString(),
              origin: urlOrigin,
              destination: urlDestination,
              departDate: urlDepartDate,
              returnDate: urlReturnDate,
              tripType: urlTripType
            });
            
            if (alternativeIds) {
              params.append('alternativeOfferIds', alternativeIds);
            }
            
            navigate(`/flights/select-fare?offerId=${flight.id}&${params.toString()}`);
          } catch (err) {
            setValidatingOfferId(null);
            const errorMsg = err.response?.data?.error || err.message;
            if (errorMsg.includes('internal_error') || errorMsg.includes('airline')) {
              alert('⚠️ This flight is temporarily unavailable from the airline. Please try another flight option.');
            } else if (errorMsg.includes('no longer available') || errorMsg.includes('expired')) {
              alert('⚠️ This flight offer has expired. Please search again for current options.');
            } else {
              alert('⚠️ Unable to select this flight. Please try another option.');
            }
            console.error('Error validating offer:', errorMsg);
          }
          return;
        }
      }
      
      // One-way flight - validate and navigate to checkout
      setValidatingOfferId(flight.id);
      
      try {
        await api.get(`/duffel/offers/${flight.id}`);
        
        const params = new URLSearchParams({
          passengers: urlPassengers.toString(),
          origin: urlOrigin,
          destination: urlDestination,
          departDate: urlDepartDate,
          tripType: urlTripType
        });
        
        navigate(`/duffel/offer/${flight.id}?${params.toString()}`);
      } catch (err) {
        setValidatingOfferId(null);
        const errorMsg = err.response?.data?.error || err.message;
        if (errorMsg.includes('internal_error') || errorMsg.includes('airline')) {
          alert('⚠️ This flight is temporarily unavailable from the airline. Please try another flight option.');
        } else if (errorMsg.includes('no longer available') || errorMsg.includes('expired')) {
          alert('⚠️ This flight offer has expired. Please search again for current options.');
        } else {
          alert('⚠️ Unable to select this flight. Please try another option.');
        }
        console.error('Error validating offer:', errorMsg);
      }
    } catch (err) {
      console.error('Unexpected error in handleSelectFlight:', err);
      alert('⚠️ An unexpected error occurred. Please try again.');
    }
  };

  if (loading) {
    return (
      <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 3 }}>
          Searching for flights...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumb and Back Button */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Breadcrumbs separator="›" aria-label="flight selection breadcrumb">
          <Typography variant="body2" color="text.secondary">
            Orders
          </Typography>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate('/deals?tab=flights')}
            sx={{ 
              textDecoration: 'none',
              color: 'primary.main',
              fontWeight: 600,
              '&:hover': { textDecoration: 'underline' },
              cursor: 'pointer',
              background: 'none',
              border: 'none',
              padding: 0,
              font: 'inherit'
            }}
          >
            New order
          </Link>
          
          {/* Outbound leg breadcrumb - clickable if on step 2 */}
          {urlTripType === 'round_trip' && selectionStep === 2 ? (
            <Link
              component="button"
              variant="body2"
              onClick={() => {
                setSelectionStep(1);
                setSelectedOffer(null);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              sx={{ 
                textDecoration: 'none',
                color: 'primary.main',
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              {urlOrigin} to {urlDestination}
            </Link>
          ) : (
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 600,
                color: 'text.primary'
              }}
            >
              {urlOrigin} to {urlDestination}
            </Typography>
          )}
          
          {/* Return leg breadcrumb - only show for round trips */}
          {urlTripType === 'round_trip' && selectionStep === 2 && (
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 600,
                color: 'text.primary'
              }}
            >
              {urlDestination} to {urlOrigin}
            </Typography>
          )}
        </Breadcrumbs>
        
        {/* Back to Deals Button */}
        <Button
          variant="outlined"
          onClick={() => navigate('/deals?tab=flights')}
          sx={{
            textTransform: 'none',
            borderColor: '#667eea',
            color: '#667eea',
            fontWeight: 600,
            '&:hover': {
              borderColor: '#5568d3',
              backgroundColor: 'rgba(102, 126, 234, 0.08)',
            }
          }}
        >
          ← Back to Deals
        </Button>
      </Box>

      {/* Search Summary / Edit Form */}
      <Paper sx={{ p: 3, mb: 3 }}>
        {/* Show selected outbound banner in step 2 */}
        {urlTripType === 'round_trip' && selectionStep === 2 && selectedOffer && (
          <Box sx={{ mb: 2, p: 2, bgcolor: '#e3f2fd', borderRadius: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="body2" color="primary" sx={{ fontWeight: 600, mb: 0.5 }}>
                ✓ Selected Outbound Flight
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedOffer.airline} • {urlOrigin} → {urlDestination} • {formatTime(selectedOffer.departure_time)} - {formatTime(selectedOffer.arrival_time)} • {selectedOffer.duration}
                {urlDepartDate && ` • ${new Date(urlDepartDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}`}
              </Typography>
            </Box>
            <Button 
              variant="outlined" 
              size="small" 
              onClick={() => {
                setSelectionStep(1);
                setSelectedOffer(null);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              Change
            </Button>
          </Box>
        )}
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            {urlTripType === 'one_way' 
              ? 'One-way flights' 
              : selectionStep === 1 
                ? `Outbound: ${urlOrigin} to ${urlDestination}`
                : `Return: ${urlDestination} to ${urlOrigin}`}
          </Typography>
          <IconButton onClick={() => setEditMode(!editMode)} size="small">
            {editMode ? <Close /> : <Edit />}
          </IconButton>
        </Box>

        {/* Collapsed view - Search summary */}
        <Collapse in={!editMode}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Chip label={`${urlOrigin} → ${urlDestination}`} />
            <Chip label={urlTripType === 'one_way' ? 'One way' : 'Return'} />
            {urlTripType === 'round_trip' && urlReturnDate ? (
              <>
                <Chip label={`Depart: ${new Date(urlDepartDate + 'T00:00:00').toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}`} />
                <Chip label={`Return: ${new Date(urlReturnDate + 'T00:00:00').toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}`} />
              </>
            ) : (
              <Chip label={new Date(urlDepartDate + 'T00:00:00').toLocaleDateString('en-US', { day: 'numeric', month: 'short' })} />
            )}
            <Chip label={`${urlPassengers} Passenger${urlPassengers > 1 ? 's' : ''}`} />
            <Chip label="Economy" />
          </Box>
        </Collapse>

        {/* Expanded view - Edit form */}
        <Collapse in={editMode}>
          <Box sx={{ mt: 2 }}>
            <FlightSearchForm
              initialOrigin={origin}
              initialDestination={destination}
              initialDepartDate={departDate}
              initialReturnDate={returnDate}
              initialPassengers={passengers}
              initialTripType={tripType}
              submitButtonText="Update Search"
              compact={true}
            />
          </Box>
        </Collapse>
      </Paper>

      {/* Price Grid - Shows price calendar for different dates */}
      {!loading && flights.length > 0 && (
        <Box sx={{ mb: 3 }}>
                <Button
            variant={showPriceGrid ? "contained" : "outlined"}
            onClick={() => setShowPriceGrid(!showPriceGrid)}
            sx={{ mb: 2 }}
          >
            {showPriceGrid ? 'Hide Price Calendar' : 'Show Price Calendar'}
                </Button>
          
          {showPriceGrid && (
            <FlightPriceGrid
              origin={urlOrigin}
              destination={urlDestination}
              departDate={urlDepartDate}
              returnDate={urlReturnDate}
              passengers={urlPassengers}
              tripType={urlTripType}
              selectionStep={selectionStep}
              onDateSelect={(newDepartDate, newReturnDate) => {
                // Trigger new search with updated dates, but keep price grid open
                const params = new URLSearchParams({
                  origin: urlOrigin,
                  destination: urlDestination,
                  departDate: newDepartDate,
                  ...(newReturnDate && urlTripType === 'round_trip' && { returnDate: newReturnDate }),
                  passengers: urlPassengers.toString(),
                  tripType: urlTripType
                });
                navigate(`/flights/search?${params.toString()}`);
                // Keep price grid open after navigation
                setShowPriceGrid(true);
              }}
            />
          )}
        </Box>
      )}

      {/* Sort & Filter Button - Mobile Only */}
      <Box sx={{ mb: 3, display: { xs: 'flex', md: 'none' }, justifyContent: 'center' }}>
        <Button
          variant="outlined"
          startIcon={<FilterList />}
          onClick={() => setFilterDrawerOpen(true)}
          sx={{
            px: 4,
            py: 1.5,
            borderRadius: 8,
            borderColor: '#e0e0e0',
            color: 'text.primary',
            fontWeight: 600,
            fontSize: '1rem',
            textTransform: 'none',
            '&:hover': {
              borderColor: '#666',
              bgcolor: 'rgba(0,0,0,0.02)',
            }
          }}
        >
          Sort & Filter
        </Button>
      </Box>

      {/* Filter Drawer */}
      <Drawer
        anchor="right"
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: 400 },
            p: 3
          }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" fontWeight={700}>
            Sort & Filter
          </Typography>
          <IconButton onClick={() => setFilterDrawerOpen(false)}>
            <Close />
          </IconButton>
        </Box>

        <Box sx={{ overflowY: 'auto', flex: 1 }}>
          <Typography variant="h6" gutterBottom fontWeight={600} sx={{ mb: 2 }}>
              Sort by
            </Typography>
            <FormControl component="fieldset" fullWidth>
              <RadioGroup value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <FormControlLabel value="least_expensive" control={<Radio />} label="Least expensive" />
              <FormControlLabel value="most_expensive" control={<Radio />} label="Most Expensive" />
              <FormControlLabel value="shortest" control={<Radio />} label="Shortest duration" />
              <FormControlLabel value="longest" control={<Radio />} label="Longest duration" />
              </RadioGroup>
            </FormControl>

            <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom fontWeight={600} sx={{ mb: 2 }}>
              Stops
            </Typography>
            <FormControl component="fieldset" fullWidth>
              <RadioGroup value={maxStops} onChange={(e) => setMaxStops(e.target.value)}>
              <FormControlLabel value="direct" control={<Radio />} label="Direct only" />
              <FormControlLabel value="1" control={<Radio />} label="1 stop at most" />
              <FormControlLabel value="2" control={<Radio />} label="2 stops at most" />
              <FormControlLabel value="any" control={<Radio />} label="Any number of stops" />
              </RadioGroup>
            </FormControl>

            <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom fontWeight={600} sx={{ mb: 2 }}>
              Airlines
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              value={selectedAirline}
              onChange={(e) => setSelectedAirline(e.target.value)}
            >
              <MenuItem value="all">All airlines</MenuItem>
              {airlines.map(airline => (
                <MenuItem key={airline} value={airline}>{airline}</MenuItem>
              ))}
            </TextField>

            <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom fontWeight={600} sx={{ mb: 2 }}>
              Flight number
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="e.g. BA123"
              value={flightNumber}
              onChange={(e) => setFlightNumber(e.target.value)}
            />

            <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom fontWeight={600} sx={{ mb: 2 }}>
              Flight time
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Time filters available after selecting flights
            </Typography>
        </Box>

        {/* Apply Button */}
        <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button
            variant="contained"
            fullWidth
            onClick={() => setFilterDrawerOpen(false)}
            sx={{
              bgcolor: '#0066FF',
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                bgcolor: '#0052CC'
              }
            }}
          >
            Apply
          </Button>
        </Box>
      </Drawer>

      <Grid container spacing={3}>
        {/* Left Sidebar - Desktop Only */}
        <Grid item xs={12} md={3} sx={{ display: { xs: 'none', md: 'block' } }}>
          <Paper sx={{ p: 3, position: 'sticky', top: 16 }}>
            <Typography variant="h6" gutterBottom fontWeight={600} sx={{ mb: 2 }}>
              Sort by
            </Typography>
            <FormControl component="fieldset" fullWidth>
              <RadioGroup value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <FormControlLabel value="least_expensive" control={<Radio size="small" />} label="Least expensive" />
                <FormControlLabel value="most_expensive" control={<Radio size="small" />} label="Most Expensive" />
                <FormControlLabel value="shortest" control={<Radio size="small" />} label="Shortest duration" />
                <FormControlLabel value="longest" control={<Radio size="small" />} label="Longest duration" />
              </RadioGroup>
            </FormControl>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom fontWeight={600} sx={{ mb: 2 }}>
              Stops
            </Typography>
            <FormControl component="fieldset" fullWidth>
              <RadioGroup value={maxStops} onChange={(e) => setMaxStops(e.target.value)}>
                <FormControlLabel value="direct" control={<Radio size="small" />} label="Direct only" />
                <FormControlLabel value="1" control={<Radio size="small" />} label="1 stop at most" />
                <FormControlLabel value="2" control={<Radio size="small" />} label="2 stops at most" />
                <FormControlLabel value="any" control={<Radio size="small" />} label="Any number of stops" />
              </RadioGroup>
            </FormControl>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom fontWeight={600} sx={{ mb: 2 }}>
              Airlines
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              value={selectedAirline}
              onChange={(e) => setSelectedAirline(e.target.value)}
            >
              <MenuItem value="all">All airlines</MenuItem>
              {airlines.map(airline => (
                <MenuItem key={airline} value={airline}>{airline}</MenuItem>
              ))}
            </TextField>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom fontWeight={600} sx={{ mb: 2 }}>
              Flight number
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="e.g. BA123"
              value={flightNumber}
              onChange={(e) => setFlightNumber(e.target.value)}
            />

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom fontWeight={600} sx={{ mb: 2 }}>
              Flight time
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Time filters available after selecting flights
            </Typography>
          </Paper>
        </Grid>

        {/* Flight Results */}
        <Grid item xs={12} md={9}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Showing {filteredFlights.length} of {flights.length} flights
            {urlTripType === 'one_way' && urlDepartDate && 
              ` for ${new Date(urlDepartDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
            }
            {urlTripType === 'round_trip' && selectionStep === 1 && urlDepartDate && 
              ` for ${new Date(urlDepartDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
            }
            {urlTripType === 'round_trip' && selectionStep === 2 && urlReturnDate && 
              ` for ${new Date(urlReturnDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
            }
          </Typography>

          {filteredFlights.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                No flights found matching your filters
              </Typography>
              <Button variant="outlined" sx={{ mt: 2 }} onClick={() => {
                setSortBy('least_expensive');
                setMaxStops('any');
                setSelectedAirline('all');
                setFlightNumber('');
                setOutboundDepartTime([0, 24]);
                setOutboundArrivalTime([0, 24]);
                setReturnDepartTime([0, 24]);
                setReturnArrivalTime([0, 24]);
              }}>
                Clear filters
              </Button>
            </Paper>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {filteredFlights.map((flight) => {
                // For round trips in step 2, show the return leg; otherwise show outbound
                const showReturnLeg = urlTripType === 'round_trip' && selectionStep === 2 && flight.return_flight;
                const legData = showReturnLeg ? flight.return_flight : flight;
                const legAirline = showReturnLeg ? flight.return_flight.airline : flight.airline;
                const legAirlineLogo = showReturnLeg ? flight.return_flight.airline_logo : flight.airline_logo;
                
                return (
                  <Card key={flight.id} sx={{ '&:hover': { boxShadow: 3 } }}>
                    <CardContent>
                      <Grid container alignItems="center" spacing={2}>
                        {/* Flight Details - Show relevant leg based on step */}
                        <Grid item xs={12} sm={7}>
                          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            <Box sx={{ width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {legAirlineLogo ? (
                                <img src={legAirlineLogo} alt={legAirline} style={{ maxWidth: '100%', maxHeight: '100%' }} />
                              ) : (
                                <Typography variant="h6">{legAirline?.substring(0, 2)}</Typography>
                              )}
                            </Box>
                            
                            <Box sx={{ flex: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                                <Typography variant="h6" fontWeight={700}>
                                  {showReturnLeg ? (
                                    <>
                                      {formatTime(legData.departure.time)} - {formatTime(legData.arrival.time)}
                                      {getDayDifference(legData.departure.time, legData.arrival.time) > 0 && (
                                        <sup style={{ fontSize: '0.6em', marginLeft: '2px' }}>
                                          +{getDayDifference(legData.departure.time, legData.arrival.time)}
                                        </sup>
                                      )}
                                    </>
                                  ) : (
                                    <>
                                      {formatTime(flight.departure_time || flight.segments?.[0]?.origin?.time)} - {formatTime(flight.arrival_time || flight.segments?.[flight.segments.length - 1]?.destination?.time)}
                                      {getDayDifference(
                                        flight.departure_time || flight.segments?.[0]?.origin?.time,
                                        flight.arrival_time || flight.segments?.[flight.segments.length - 1]?.destination?.time
                                      ) > 0 && (
                                        <sup style={{ fontSize: '0.6em', marginLeft: '2px' }}>
                                          +{getDayDifference(
                                            flight.departure_time || flight.segments?.[0]?.origin?.time,
                                            flight.arrival_time || flight.segments?.[flight.segments.length - 1]?.destination?.time
                                          )}
                                        </sup>
                                      )}
                                    </>
                                  )}
                                </Typography>
                              </Box>
                              
                              <Typography variant="body2" color="text.secondary">
                                {legAirline} · {showReturnLeg ? legData.flight_number : `${flight.segments?.[0]?.marketing_carrier_code}${flight.segments?.[0]?.marketing_flight_number}`}
                              </Typography>
                              
                              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                  <Schedule fontSize="inherit" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                                  {showReturnLeg ? legData.duration : flight.duration}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {showReturnLeg 
                                    ? `${legData.origin} – ${legData.destination}`
                                    : `${flight.origin || flight.segments?.[0]?.origin?.airport} – ${flight.destination || flight.segments?.[flight.segments.length - 1]?.destination?.airport}`
                                  }
                                </Typography>
                                <Chip 
                                  label={
                                    showReturnLeg 
                                      ? (legData.stops === 0 ? 'Non-stop' : `${legData.stops} stop${legData.stops > 1 ? 's' : ''}`)
                                      : (flight.stops === 0 ? 'Non-stop' : `${flight.stops} stop${flight.stops > 1 ? 's' : ''}`)
                                  }
                                  size="small" 
                                  sx={{ height: 20 }}
                                />
                              </Box>
                            </Box>
                          </Box>
                        </Grid>

                        {/* Price & Select Button */}
                        <Grid item xs={12} sm={5}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                {flight.fareCount > 1 ? 'From' : (urlTripType === 'round_trip' ? 'Total' : 'From')}
                              </Typography>
                              <Typography variant="h5" fontWeight={700}>
                                {formatPrice(parseFloat(flight.lowestPrice || flight.total_amount), flight.total_currency)}
                              </Typography>
                              {flight.fareCount > 1 && (
                                <Chip 
                                  label={`${flight.fareCount} fare options`}
                                  size="small"
                                  sx={{ 
                                    mt: 0.5,
                                    bgcolor: '#4CAF50',
                                    color: 'white',
                                    fontSize: '0.7rem',
                                    height: 20
                                  }}
                                />
                              )}
                            </Box>
                            <Button
                              variant="contained"
                              onClick={() => handleSelectFlight(flight)}
                              disabled={validatingOfferId === flight.id}
                              sx={{
                                bgcolor: 'black',
                                color: 'white',
                                px: 4,
                                '&:hover': {
                                  bgcolor: '#333'
                                }
                              }}
                            >
                              {validatingOfferId === flight.id ? (
                                <>
                                  <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />
                                  Validating...
                                </>
                              ) : (
                                flight.fareCount > 1 ? 'View Fares' : 'Select'
                              )}
                            </Button>
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          )}
        </Grid>
      </Grid>
    </Container>
  );
}

export default FlightSearchResults;

